import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ShadowConfig } from '@/scenes/engine/common/interfaces'
import { AdjustmentsConfig, defaultAdjustments } from '@/services/batchEdit/adjustCss'
import { PositionConfig } from '@/services/batchEdit/positionFit'
import {
  BatchBadgeAsset,
  BatchBadgeConfig,
  BatchBadgeLayout,
} from '@/services/batchEdit/badge'
import { CustomBackgroundRecord } from '@/services/batchEdit/customBackgroundsStorage'
import type { WhatshotListingContext } from '@/utils/whatshotListingContext'
import {
  compactBatchSectionMembership,
  findBatchSectionBySourceAffinity,
  getBatchSectionInsertionIndex,
  getBatchSectionMemberIds,
  moveBatchSectionItems,
  reorderBatchItems,
} from './sectionOperations'

export type BatchItemStatus = 'idle' | 'processing' | 'processingRemoveBg' | 'processingEditWithAi' | 'done' | 'error'

/** Any of the in-flight statuses — a card in one of these isn't ready to select/edit yet. */
export function isProcessingStatus(status: BatchItemStatus): boolean {
  return status === 'processing' || status === 'processingRemoveBg' || status === 'processingEditWithAi'
}

/**
 * Bounding box of the opaque subject within a cutout, expressed as fractions
 * (0..1) of the cutout's pixel dimensions. Used to frame the subject when
 * resizing so transparent margins don't inflate the padding.
 */
export interface NormalizedBBox {
  x: number
  y: number
  width: number
  height: number
}

export interface BatchImage {
  id: string
  name: string
  /** Optional seller narration supplied by a WhatShot handoff for CSV export. */
  whatshotListingContext?: WhatshotListingContext
  /** Structural group membership. Undefined means the image is in a loose run. */
  sectionId?: string
  /** The imported source image as a data URL. */
  originalDataUrl: string
  /** Versioned removal masks (WHITE = keep), the source of truth for bg-removal/erase. */
  masks?: Record<number, string>
  /** Selected mask version (index into `masks`). Undefined = no removal. */
  maskVersion?: number
  /** Cutout composited from original x mask, cached for display. Derived — not persisted. */
  cutoutDataUrl?: string | null
  /** Normalized (0..1) bounding box of the subject, computed from the mask. Null until computed. */
  cutoutBBox?: NormalizedBBox | null
  /** Intrinsic pixel width the bbox is normalized against (the mask's own size). */
  cutoutWidth?: number
  /** Intrinsic pixel height the bbox is normalized against. */
  cutoutHeight?: number
  /** Whether the background-removed cutout is currently shown (reversible switch). */
  bgRemoved: boolean
  /** Live CSS background color shown behind the cutout. Null => explicit transparent; undefined/new images default to white. */
  bgColor?: string | null
  /** Id (content hash) of a user-uploaded background image, into `BatchState.bgImageMap`. Mutually exclusive with bgColor. */
  bgImageId?: string | null
  /** Resolved bytes for `bgImageId`, from `bgImageMap`. Derived — not persisted. */
  bgImageSrc?: string | null
  /** CSS drop-shadow applied to the cutout (only visible when bg removed). Null = none. */
  shadow?: ShadowConfig | null
  /** CSS-filter image adjustments (exposure/contrast/etc). Null = none. */
  adjustments?: AdjustmentsConfig | null
  /** Object placement/padding (bbox framing). Null/undefined = Center 10% default. */
  position?: PositionConfig | null
  /** Optional corner badge flattened only at export time. */
  badge?: BatchBadgeConfig | null
  /** Resolved upload bytes for an upload badge. Derived from BatchState.badgeAssets. */
  badgeAsset?: BatchBadgeAsset
  /** Target export width from the resize preset. Undefined = original image dimensions. */
  targetWidth?: number
  /** Target export height from the resize preset. Undefined = original image dimensions. */
  targetHeight?: number
  /** Aspect ratio (width / height) of the resize preset. Undefined = original image ratio. */
  aspectRatio?: number
  /** Intrinsic aspect ratio (width / height) of the original uploaded image. */
  originalAspectRatio?: number
  /** Intrinsic pixel width of the original uploaded image. */
  originalWidth?: number
  /** Intrinsic pixel height of the original uploaded image. */
  originalHeight?: number
  status: BatchItemStatus
  error?: string | null
}

/** Partial fill-in for a placeholder card, as a deferred import resolves size then bytes. */
export interface BatchImageDataPatch {
  id: string
  originalDataUrl?: string
  originalWidth?: number
  originalHeight?: number
  originalAspectRatio?: number
}

/**
 * Permanent image bytes, keyed by id. Kept while reachable by current/history edit stacks.
 */
export interface BatchImageData {
  id: string
  name: string
  originalDataUrl: string
  /**
   * Append-only stack of removal masks (WHITE = keep). Each bg-removal/erase adds
   * a new entry instead of overwriting, so undo can point `BatchImageEdit.maskVersion`
   * back at an earlier one. Never snapshotted — GC'd by pruneUnreferencedData.
   */
  masks?: Record<number, string>
  /** Next version key to assign in `masks`. */
  nextMaskVersion?: number
  originalAspectRatio?: number
  originalWidth?: number
  originalHeight?: number
}

/** A composited cutout plus the subject bbox computed from its mask, for one mask version. */
export interface CutoutCacheEntry {
  url: string
  /** Normalized (0..1) bounding box of the subject, computed from the mask. Null if computation failed. */
  bbox: NormalizedBBox | null
  /** Intrinsic pixel width the bbox is normalized against (the mask's own size). Undefined if computation failed. */
  width?: number
  /** Intrinsic pixel height the bbox is normalized against. Undefined if computation failed. */
  height?: number
}

/** Derived cutout cache, per id: mask version -> composited cutout + bbox. */
export type CutoutCache = Record<number, CutoutCacheEntry>

export function resolveCutout(
  versions: CutoutCache | undefined,
  maskVersion: number | undefined,
): CutoutCacheEntry | null {
  if (!versions || maskVersion == null) return null
  return versions[maskVersion] ?? null
}

/**
 * Lightweight, per-image edit state — the only thing history snapshots.
 * All scalars, no data URLs.
 */
export interface BatchImageEdit {
  id: string
  sectionId?: string
  bgRemoved: boolean
  bgColor?: string | null
  /** Scalar pointer (content-hash id) into `BatchState.bgImageMap` — a scalar, so
   *  it IS snapshotted, the same trick `maskVersion` uses to make bg-removal
   *  undoable without putting image bytes in the history stack. */
  bgImageId?: string | null
  shadow?: ShadowConfig | null
  adjustments?: AdjustmentsConfig | null
  position?: PositionConfig | null
  badge?: BatchBadgeConfig | null
  targetWidth?: number
  targetHeight?: number
  aspectRatio?: number
  /**
   * Mask version pointer (key into `BatchImageData.masks`). A scalar, so it IS
   * snapshotted — that's what makes bg-removal/erase undoable: mask bytes stay in
   * `dataMap` while history moves this pointer.
   */
  maskVersion?: number
  status: BatchItemStatus
  error?: string | null
}

export type BatchSectionNameOrigin = 'fallback' | 'ai' | 'user'

export interface BatchSection {
  id: string
  name: string
  nameOrigin: BatchSectionNameOrigin
  /** Image used to derive the background AI name. This is not generation affinity. */
  namingSourceImageId?: string
  /** Present only while the first generated result can still fail and roll the section back. */
  pendingGenerationSourceImageId?: string
  previousSourceSectionId?: string
  previousSourceAffinitySectionId?: string
  previousSourceIndex?: number
  previousSourceSectionIndex?: number
  previousSourcePreviousImageId?: string
  previousSourceNextImageId?: string
}

export interface BatchHistorySnapshot {
  images: BatchImageEdit[]
  sections: Record<string, BatchSection>
  sourceSectionAffinityByImageId: Record<string, string>
}

export interface BatchState {
  /** Permanent image data, keyed by id. Never snapshotted. */
  dataMap: Record<string, BatchImageData>
  /** Ordered edit state = display order. The list history snapshots. */
  images: BatchImageEdit[]
  sections: Record<string, BatchSection>
  /** Stable generation destination per source. Missing section ids are intentional tombstones. */
  sourceSectionAffinityByImageId: Record<string, string>
  selectedIds: string[]
  selectedSectionId: string | null
  /** Monotonic within one batch; undo/delete never reuses a visible fallback number. */
  nextSectionOrdinal: number
  /** Past structural snapshots (oldest first). */
  past: BatchHistorySnapshot[]
  /** Future structural snapshots (next-to-redo first). */
  future: BatchHistorySnapshot[]
  /** Derived cutout cache, keyed by id. Not snapshotted (recomputed on demand). */
  cutoutCache: Record<string, CutoutCache>
  /** Upload badge bytes, shared across images/history entries by asset id. */
  badgeAssets: Record<string, BatchBadgeAsset>
  /** User-uploaded background image bytes, keyed by content-hash id. Never
   *  snapshotted — like `dataMap`, only the `bgImageId` pointer is. */
  bgImageMap: Record<string, CustomBackgroundRecord>
  /**
   * Ids reachable from the Custom-backgrounds list or any saved preset — i.e.
   * referenced from OUTSIDE the edit-state/history graph. `pruneUnreferencedData`
   * unions this with what it finds in images/past/future before deciding what's
   * safe to drop from `bgImageMap`.
   */
  pinnedBgImageIds: string[]
  /**
   * Upload-badge assetIds reachable from OUTSIDE the edit-state/history graph —
   * the Custom badges list or any saved preset. Mirrors `pinnedBgImageIds`;
   * `pruneUnreferencedData` unions this with what it finds in images/past/future
   * before deciding what's safe to drop from `badgeAssets`.
   */
  pinnedBadgeAssetIds: string[]
}

/** Maximum number of undo (and redo) steps kept in memory. */
const MAX_HISTORY = 20

/** Split a full BatchImage into its permanent data, edit state, and resolved upload asset. */
function splitImage(img: BatchImage): {
  data: BatchImageData
  edit: BatchImageEdit
  badgeAsset?: BatchBadgeAsset
} {
  const badgeAsset = img.badge?.type === 'upload' && img.badgeAsset?.id === img.badge.assetId
    ? img.badgeAsset
    : undefined
  return {
    data: {
      id: img.id,
      name: img.name,
      originalDataUrl: img.originalDataUrl,
      masks: img.masks ? { ...img.masks } : {},
      nextMaskVersion: img.masks ? maxKey(img.masks) + 1 : 0,
      originalAspectRatio: img.originalAspectRatio,
      originalWidth: img.originalWidth,
      originalHeight: img.originalHeight,
    },
    edit: {
      id: img.id,
      sectionId: img.sectionId,
      bgRemoved: img.bgRemoved,
      bgColor: img.bgColor ?? null,
      bgImageId: img.bgImageId ?? null,
      shadow: img.shadow ?? null,
      adjustments: img.adjustments ?? null,
      position: img.position ?? null,
      badge: img.badge ?? null,
      targetWidth: img.targetWidth,
      targetHeight: img.targetHeight,
      aspectRatio: img.aspectRatio,
      maskVersion: img.maskVersion,
      status: img.status,
      error: img.error ?? null,
    },
    badgeAsset,
  }
}

function registerSplitBadgeAsset(
  state: BatchState,
  badgeAsset?: BatchBadgeAsset,
): void {
  if (badgeAsset) state.badgeAssets[badgeAsset.id] = badgeAsset
}

/** Largest numeric key in a version map (−1 when empty). */
function maxKey(map: Record<number, unknown>): number {
  let max = -1
  for (const key of Object.keys(map)) {
    const n = Number(key)
    if (n > max) max = n
  }
  return max
}

/** Deep-enough copy of an edit list (entries are flat scalars).
 *
 *  Import / bg-removal in-flight statuses are reset to 'idle' at snapshot time —
 *  restoring them would show a spinner with no live API call behind it.
 *
 *  `processingEditWithAi` is preserved: Batch Edit-with-AI is fire-and-forget and
 *  undo is allowed while a generation runs, so a snapshot taken mid-flight must
 *  still know the placeholder is a live loader (not a blank idle card).
 *
 *  Byte-less tiles (import / Edit-with-AI placeholders) are KEPT in the snapshot.
 *  Dropping them here caused data loss: a later pushHistory while a generation was
 *  still loading omitted the tile, so once bytes landed an undo restored a stack
 *  that never mentioned it and pruneUnreferencedData deleted the completed image.
 *  Still-empty import placeholders are stripped on restore; in-flight Edit-with-AI
 *  placeholders are kept (see sanitizeRestoredEdits). */
function snapshotEdits(edits: BatchImageEdit[]): BatchImageEdit[] {
  return edits.map((e) =>
    isProcessingStatus(e.status) && e.status !== 'processingEditWithAi'
      ? { ...e, status: 'idle' as const, error: null }
      : { ...e },
  )
}

function snapshotSections(sections: Record<string, BatchSection>): Record<string, BatchSection> {
  return Object.fromEntries(
    Object.entries(sections).map(([id, section]) => [id, { ...section }]),
  )
}

function snapshotBatch(
  state: Pick<BatchState, 'images' | 'sections' | 'sourceSectionAffinityByImageId'>,
): BatchHistorySnapshot {
  return {
    images: snapshotEdits(state.images),
    sections: snapshotSections(state.sections),
    sourceSectionAffinityByImageId: { ...state.sourceSectionAffinityByImageId },
  }
}

function resolveSectionName(
  state: BatchState,
  sectionId: string,
  name: string,
): void {
  const current = state.sections[sectionId]
  if (!current || current.nameOrigin !== 'fallback') return
  const apply = (sections: Record<string, BatchSection>) => {
    const section = sections[sectionId]
    if (!section || section.nameOrigin !== 'fallback') return
    section.name = name
    section.nameOrigin = 'ai'
  }
  apply(state.sections)
  for (const snapshot of state.past) apply(snapshot.sections)
  for (const snapshot of state.future) apply(snapshot.sections)
}

/** Keep structural metadata consistent after delete, move, abort, or restore. */
function pruneEmptySections(
  images: BatchImageEdit[],
  sections: Record<string, BatchSection>,
  sourceSectionAffinityByImageId: Record<string, string>,
): void {
  const liveImageIds = new Set(images.map((image) => image.id))
  const referencedSectionIds = new Set<string>()
  for (const image of images) {
    if (image.sectionId && sections[image.sectionId]) referencedSectionIds.add(image.sectionId)
    else if (image.sectionId) image.sectionId = undefined
  }
  for (const sectionId of Object.keys(sections)) {
    if (!referencedSectionIds.has(sectionId)) {
      delete sections[sectionId]
      continue
    }
    const namingSourceId = sections[sectionId].namingSourceImageId
    if (namingSourceId && !liveImageIds.has(namingSourceId)) {
      sections[sectionId].namingSourceImageId = undefined
    }
  }
  for (const sourceId of Object.keys(sourceSectionAffinityByImageId)) {
    if (!liveImageIds.has(sourceId)) delete sourceSectionAffinityByImageId[sourceId]
  }
}

function clearMissingSelectedSection(
  state: Pick<BatchState, 'selectedSectionId' | 'sections'>,
): void {
  if (state.selectedSectionId && !state.sections[state.selectedSectionId]) {
    state.selectedSectionId = null
  }
}

function rollbackFailedAutomaticSections(
  images: BatchImageEdit[],
  sections: Record<string, BatchSection>,
  sourceSectionAffinityByImageId: Record<string, string>,
): void {
  for (const [sectionId, section] of Object.entries(sections)) {
    const sourceId = section.pendingGenerationSourceImageId
    if (!sourceId) continue
    const hasGeneratedMember = images.some((image) => (
      image.sectionId === sectionId && image.id !== sourceId
    ))
    if (hasGeneratedMember) continue

    const source = images.find((image) => image.id === sourceId)
    const sourceStillInPendingSection = source?.sectionId === sectionId
    if (sourceStillInPendingSection) {
      const restoredSectionId = section.previousSourceSectionId && sections[section.previousSourceSectionId]
        ? section.previousSourceSectionId
        : undefined
      source.sectionId = restoredSectionId
      const currentIndex = images.indexOf(source)
      images.splice(currentIndex, 1)
      let insertionIndex = Math.min(section.previousSourceIndex ?? images.length, images.length)
      if (restoredSectionId && section.previousSourceSectionIndex != null) {
        const restoredMembers = images.filter((image) => image.sectionId === restoredSectionId)
        const nextMember = restoredMembers[section.previousSourceSectionIndex]
        insertionIndex = nextMember
          ? images.indexOf(nextMember)
          : getBatchSectionInsertionIndex(images, restoredSectionId)
      } else if (!restoredSectionId) {
        const nextImageIndex = images.findIndex((image) => image.id === section.previousSourceNextImageId)
        const previousImageIndex = images.findIndex((image) => image.id === section.previousSourcePreviousImageId)
        if (nextImageIndex !== -1) insertionIndex = nextImageIndex
        else if (previousImageIndex !== -1) insertionIndex = previousImageIndex + 1
      }
      images.splice(insertionIndex, 0, source)
    }
    if (section.previousSourceAffinitySectionId) {
      sourceSectionAffinityByImageId[sourceId] = section.previousSourceAffinitySectionId
    } else {
      delete sourceSectionAffinityByImageId[sourceId]
    }
    delete sections[sectionId]
  }
}

function settleAutomaticSection(section: BatchSection | undefined): void {
  if (!section) return
  section.pendingGenerationSourceImageId = undefined
  section.previousSourceSectionId = undefined
  section.previousSourceAffinitySectionId = undefined
  section.previousSourceIndex = undefined
  section.previousSourceSectionIndex = undefined
  section.previousSourcePreviousImageId = undefined
  section.previousSourceNextImageId = undefined
}

/**
 * After restoring a snapshot: drop tiles whose bytes never landed (would render as
 * blank cards with no loader), except in-flight Edit-with-AI placeholders — those
 * keep their loader so a generation that outlives an undo of a later edit can
 * still resolve into the live list instead of being pruned away.
 *
 * A tile that DOES have bytes is never left mid-processing, `processingEditWithAi`
 * included: bytes only exist once `resolveBatchImagesData` ran, i.e. the generation
 * already finished, so keeping the marker would render a spinner forever over a
 * finished image. Terminal failures scrub their own snapshots (see
 * `settleHistoryPlaceholders`) so the byte-less branch above only ever holds
 * genuinely live runs.
 *
 * Mutates `edits` statuses in place; returns a filtered array for assignment to
 * `state.images`. Caller must have already detached `edits` from past/future so
 * Immer never sees the same object in two places on the draft.
 */
function sanitizeRestoredEdits(
  edits: BatchImageEdit[],
  dataMap: Record<string, BatchImageData>,
): BatchImageEdit[] {
  return edits.filter((e) => {
    if (!dataMap[e.id]?.originalDataUrl) {
      return e.status === 'processingEditWithAi'
    }
    if (isProcessingStatus(e.status)) {
      e.status = 'idle'
      e.error = null
    }
    return true
  })
}

/** Every (id, maskVersion) reachable from the live edit list or any history snapshot. */
function collectReferences(state: BatchState): Map<string, Set<number>> {
  const refs = new Map<string, Set<number>>()
  const visit = (edits: BatchImageEdit[]) => {
    for (const edit of edits) {
      let versions = refs.get(edit.id)
      if (!versions) {
        versions = new Set<number>()
        refs.set(edit.id, versions)
      }
      if (edit.maskVersion != null) versions.add(edit.maskVersion)
    }
  }
  visit(state.images)
  for (const snapshot of state.past) visit(snapshot.images)
  for (const snapshot of state.future) visit(snapshot.images)
  return refs
}

/** Every bgImageId reachable from the live edit list or any history snapshot. */
function collectReferencedBgImageIds(state: BatchState): Set<string> {
  const refs = new Set<string>()
  const visit = (edits: BatchImageEdit[]) => {
    for (const edit of edits) {
      if (edit.bgImageId) refs.add(edit.bgImageId)
    }
  }
  visit(state.images)
  for (const snapshot of state.past) visit(snapshot.images)
  for (const snapshot of state.future) visit(snapshot.images)
  return refs
}

function pruneUnreferencedData(state: BatchState): void {
  const refs = collectReferences(state)
  const badgeAssetRefs = new Set<string>()
  const collectBadgeAssetRefs = (edits: BatchImageEdit[]) => {
    for (const edit of edits) {
      if (edit.badge?.type === 'upload') badgeAssetRefs.add(edit.badge.assetId)
    }
  }
  collectBadgeAssetRefs(state.images)
  for (const snapshot of state.past) collectBadgeAssetRefs(snapshot.images)
  for (const snapshot of state.future) collectBadgeAssetRefs(snapshot.images)

  for (const id of Object.keys(state.dataMap)) {
    const versions = refs.get(id)
    if (!versions) {
      // No live or history edit references this image at all.
      delete state.dataMap[id]
      delete state.cutoutCache[id]
      continue
    }
    // Drop mask versions no snapshot can restore anymore.
    const masks = state.dataMap[id].masks
    if (masks) {
      for (const key of Object.keys(masks)) {
        if (!versions.has(Number(key))) delete masks[Number(key)]
      }
    }
    const cached = state.cutoutCache[id]
    if (cached) {
      for (const key of Object.keys(cached)) {
        if (!versions.has(Number(key))) delete cached[Number(key)]
      }
    }
  }
  // Drop cache entries for ids that no longer exist.
  for (const id of Object.keys(state.cutoutCache)) {
    if (!refs.has(id)) delete state.cutoutCache[id]
  }
  // A badge asset is kept while reachable from live/history edit state OR
  // pinned from outside it — the Custom badges list or a saved preset.
  for (const pinned of state.pinnedBadgeAssetIds) badgeAssetRefs.add(pinned)
  for (const assetId of Object.keys(state.badgeAssets)) {
    if (!badgeAssetRefs.has(assetId)) delete state.badgeAssets[assetId]
  }

  // A background image is kept while reachable from live/history edit state
  // (collectReferencedBgImageIds) OR pinned from outside it — the Custom list
  // or a saved preset (pinnedBgImageIds, kept in sync by the session hook).
  const bgImageRefs = collectReferencedBgImageIds(state)
  for (const pinned of state.pinnedBgImageIds) bgImageRefs.add(pinned)
  for (const id of Object.keys(state.bgImageMap)) {
    if (!bgImageRefs.has(id)) delete state.bgImageMap[id]
  }
}

const initialState: BatchState = {
  dataMap: {},
  images: [],
  sections: {},
  sourceSectionAffinityByImageId: {},
  selectedIds: [],
  selectedSectionId: null,
  nextSectionOrdinal: 0,
  past: [],
  future: [],
  cutoutCache: {},
  badgeAssets: {},
  bgImageMap: {},
  pinnedBgImageIds: [],
  pinnedBadgeAssetIds: [],
}

const batchSlice = createSlice({
  name: 'batch',
  initialState,
  reducers: {
    setBatchImages(state, { payload }: PayloadAction<BatchImage[]>) {
      state.dataMap = {}
      state.images = []
      state.sections = {}
      state.sourceSectionAffinityByImageId = {}
      state.selectedSectionId = null
      state.nextSectionOrdinal = 0
      state.cutoutCache = {}
      // `badgeAssets` is deliberately NOT cleared, mirroring `bgImageMap`:
      // both are content-addressed byte caches for a persistent user gallery,
      // kept alive by `pinnedBadgeAssetIds`/`pinnedBgImageIds` rather than by
      // the current image list. Clearing here dropped bytes the Custom-badges
      // gallery had already hydrated (nothing re-registers them until that
      // session hook remounts), so an imported batch could not resolve a badge
      // picked from the gallery. `pruneUnreferencedData` — which honours the
      // pins — is the only thing that should ever remove an entry.
      for (const img of payload) {
        const { data, edit, badgeAsset } = splitImage(img)
        edit.sectionId = undefined
        state.dataMap[data.id] = data
        state.images.push(edit)
        registerSplitBadgeAsset(state, badgeAsset)
      }
      state.selectedIds = []
      state.past = []
      state.future = []
    },
    addBatchImages(state, { payload }: PayloadAction<BatchImage[]>) {
      // History is pushed by the caller (pushHistory) before dispatching this.
      for (const img of payload) {
        const { data, edit, badgeAsset } = splitImage(img)
        state.dataMap[data.id] = data
        state.images.push(edit)
        registerSplitBadgeAsset(state, badgeAsset)
      }
    },
    insertBatchImagesAfter(
      state,
      { payload }: PayloadAction<Array<{ afterId: string; image: BatchImage }>>,
    ) {
      // History is pushed by the caller (pushHistory) before dispatching this.
      // Process in order so each duplicate lands immediately after its original,
      // even as earlier insertions shift subsequent indices.
      for (const { afterId, image } of payload) {
        const { data, edit, badgeAsset } = splitImage(image)
        state.dataMap[data.id] = data
        registerSplitBadgeAsset(state, badgeAsset)
        const insertIndex = state.images.findIndex(img => img.id === afterId)
        if (insertIndex === -1) {
          state.images.push(edit)
        } else {
          state.images.splice(insertIndex + 1, 0, edit)
        }
      }
    },
    /**
     * Fills in placeholder cards minted by a deferred import (see
     * `services/batchEdit/batchImport.ts`). Status is left alone: placeholders start
     * at 'processing' and hand over to auto bg-removal, so a card never flashes an
     * un-loadered preview. Plural because patches arrive in bursts. Unknown ids are
     * skipped — a card can be deleted, or the batch replaced, mid-read.
     */
    resolveBatchImagesData(state, { payload }: PayloadAction<BatchImageDataPatch[]>) {
      for (const patch of payload) {
        const data = state.dataMap[patch.id]
        if (!data) continue
        if (patch.originalDataUrl !== undefined) data.originalDataUrl = patch.originalDataUrl
        if (patch.originalWidth !== undefined) data.originalWidth = patch.originalWidth
        if (patch.originalHeight !== undefined) data.originalHeight = patch.originalHeight
        if (patch.originalAspectRatio !== undefined) {
          data.originalAspectRatio = patch.originalAspectRatio
        }
      }
    },
    /**
     * Atomically lands generated bytes only when the placeholder is still live.
     * If undo/delete won the race, scrub the stale placeholder from both history
     * stacks so redo cannot resurrect completed work the user removed.
     */
    landGeneratedBatchImage(
      state,
      { payload }: PayloadAction<{
        patch: Required<Pick<BatchImageDataPatch, 'id' | 'originalDataUrl' | 'originalWidth' | 'originalHeight' | 'originalAspectRatio'>>
        /** Omitted for generated imports that are not derived from a Batch card. */
        sourceId?: string
        createdSectionId?: string
        sectionNameHint?: string | null
      }>,
    ) {
      const edit = state.images.find((image) => image.id === payload.patch.id)
      if (!edit) {
        const keep = (image: BatchImageEdit) => image.id !== payload.patch.id
        const scrub = (snapshot: BatchHistorySnapshot): BatchHistorySnapshot => {
          const next: BatchHistorySnapshot = {
            images: snapshot.images.filter(keep),
            sections: snapshotSections(snapshot.sections),
            sourceSectionAffinityByImageId: { ...snapshot.sourceSectionAffinityByImageId },
          }
          // Mirror discardBatchImages: only roll back an empty automatic section.
          // Dissolving every member of createdSectionId would strip settled siblings
          // from undo/redo snapshots (e.g. delete-section-while-loading → late land).
          rollbackFailedAutomaticSections(
            next.images,
            next.sections,
            next.sourceSectionAffinityByImageId,
          )
          if (
            payload.sourceId &&
            payload.createdSectionId &&
            next.sourceSectionAffinityByImageId[payload.sourceId] === payload.createdSectionId
            && !next.sections[payload.createdSectionId]
          ) {
            const currentAffinity = state.sourceSectionAffinityByImageId[payload.sourceId]
            if (currentAffinity && currentAffinity !== payload.createdSectionId) {
              next.sourceSectionAffinityByImageId[payload.sourceId] = currentAffinity
            } else {
              delete next.sourceSectionAffinityByImageId[payload.sourceId]
            }
          }
          pruneEmptySections(next.images, next.sections, next.sourceSectionAffinityByImageId)
          return next
        }
        state.past = state.past.map(scrub)
        state.future = state.future.map(scrub)
        pruneUnreferencedData(state)
        return
      }
      const data = state.dataMap[payload.patch.id]
      if (!data) return
      data.originalDataUrl = payload.patch.originalDataUrl
      data.originalWidth = payload.patch.originalWidth
      data.originalHeight = payload.patch.originalHeight
      data.originalAspectRatio = payload.patch.originalAspectRatio
      edit.status = 'done'
      edit.error = null
      const landedSectionId = edit.sectionId
      if (landedSectionId) {
        settleAutomaticSection(state.sections[landedSectionId])
        for (const snapshot of state.past) settleAutomaticSection(snapshot.sections[landedSectionId])
        for (const snapshot of state.future) settleAutomaticSection(snapshot.sections[landedSectionId])
      }
      if (payload.sectionNameHint && payload.sourceId) {
        const affinitySection = findBatchSectionBySourceAffinity(
          state.sections,
          state.sourceSectionAffinityByImageId,
          payload.sourceId,
        )
        const section = affinitySection ?? (
          payload.createdSectionId ? state.sections[payload.createdSectionId] : undefined
        )
        if (section) resolveSectionName(state, section.id, payload.sectionNameHint)
      }
    },
    clearBatch(state) {
      state.dataMap = {}
      state.images = []
      state.sections = {}
      state.sourceSectionAffinityByImageId = {}
      state.selectedSectionId = null
      state.nextSectionOrdinal = 0
      state.cutoutCache = {}
      // Left intact for the same reason as in setBatchImages above.
      state.selectedIds = []
      state.past = []
      state.future = []
    },
    toggleSelect(state, { payload: id }: PayloadAction<string>) {
      state.selectedSectionId = null
      const index = state.selectedIds.indexOf(id)
      if (index === -1) state.selectedIds.push(id)
      else state.selectedIds.splice(index, 1)
    },
    setSelected(state, { payload }: PayloadAction<string[]>) {
      state.selectedIds = payload
      state.selectedSectionId = null
    },
    selectBatchImageIfLive(state, { payload: id }: PayloadAction<string>) {
      if (!state.images.some((image) => image.id === id)) return
      state.selectedIds = [id]
      state.selectedSectionId = null
    },
    clearSelection(state) {
      state.selectedIds = []
      state.selectedSectionId = null
    },
    selectSection(state, { payload: sectionId }: PayloadAction<string | null>) {
      state.selectedSectionId = sectionId && state.sections[sectionId] ? sectionId : null
      state.selectedIds = []
    },
    selectSectionImages(state, { payload: sectionId }: PayloadAction<string>) {
      if (!state.sections[sectionId]) return
      state.selectedSectionId = sectionId
      state.selectedIds = state.images
        .filter((image) => image.sectionId === sectionId && !isProcessingStatus(image.status))
        .map((image) => image.id)
    },
    createBatchSection(
      state,
      { payload }: PayloadAction<{ sectionId: string; imageIds: string[] }>,
    ) {
      const pickedIds = new Set(payload.imageIds)
      const firstIndex = state.images.findIndex((image) => pickedIds.has(image.id))
      if (firstIndex === -1 || state.sections[payload.sectionId]) return
      const picked = state.images.filter((image) => pickedIds.has(image.id))
      if (!picked.length) return
      const requestedSourceIds = new Set(payload.imageIds)
      const rest = state.images.filter((image) => !pickedIds.has(image.id))
      const insertionIndex = state.images.slice(0, firstIndex).filter((image) => !pickedIds.has(image.id)).length
      for (const image of picked) image.sectionId = payload.sectionId
      state.images = compactBatchSectionMembership([
        ...rest.slice(0, insertionIndex),
        ...picked,
        ...rest.slice(insertionIndex),
      ])
      state.nextSectionOrdinal += 1
      state.sections[payload.sectionId] = {
        id: payload.sectionId,
        name: `Product ${state.nextSectionOrdinal}`,
        nameOrigin: 'fallback',
        namingSourceImageId: picked.find((image) => requestedSourceIds.has(image.id))?.id,
      }
      for (const image of picked) {
        if (requestedSourceIds.has(image.id) && !state.sourceSectionAffinityByImageId[image.id]) {
          state.sourceSectionAffinityByImageId[image.id] = payload.sectionId
        }
      }
      state.selectedIds = []
      state.selectedSectionId = null
      pruneEmptySections(state.images, state.sections, state.sourceSectionAffinityByImageId)
    },
    renameBatchSection(
      state,
      { payload }: PayloadAction<{ sectionId: string; name: string }>,
    ) {
      const section = state.sections[payload.sectionId]
      const name = payload.name.trim()
      if (!section || !name || section.name === name) return
      section.name = name
      section.nameOrigin = 'user'
    },
    claimBatchSectionName(state, { payload: sectionId }: PayloadAction<string>) {
      const section = state.sections[sectionId]
      if (section?.nameOrigin === 'fallback') section.nameOrigin = 'user'
    },
    resolveBatchSectionName(
      state,
      { payload }: PayloadAction<{ sectionId: string; name: string }>,
    ) {
      const name = payload.name.trim()
      if (!name) return
      resolveSectionName(state, payload.sectionId, name)
    },
    resolveBatchSectionNameBySource(
      state,
      { payload }: PayloadAction<{ sourceId: string; name: string }>,
    ) {
      const name = payload.name.trim()
      if (!name) return
      const section = findBatchSectionBySourceAffinity(
        state.sections,
        state.sourceSectionAffinityByImageId,
        payload.sourceId,
      )
      if (section) resolveSectionName(state, section.id, name)
    },
    moveBatchImages(
      state,
      { payload }: PayloadAction<{ imageIds: string[]; beforeId: string | null; destinationSectionId: string | null }>,
    ) {
      const validSectionId = payload.destinationSectionId && state.sections[payload.destinationSectionId]
        ? payload.destinationSectionId
        : undefined
      const result = moveBatchSectionItems(
        state.images,
        payload.imageIds,
        payload.beforeId,
        validSectionId,
      )
      if (!result.changed) return
      state.images = result.items
      pruneEmptySections(state.images, state.sections, state.sourceSectionAffinityByImageId)
      clearMissingSelectedSection(state)
    },
    reorderBatchSection(
      state,
      { payload }: PayloadAction<{ sectionId: string; beforeId: string | null }>,
    ) {
      if (!state.sections[payload.sectionId]) return
      const movingIds = getBatchSectionMemberIds(state.images, payload.sectionId)
      const result = reorderBatchItems(state.images, movingIds, payload.beforeId)
      if (result.changed) state.images = result.items
    },
    insertGeneratedBatchImage(
      state,
      { payload }: PayloadAction<{ sourceId: string; image: BatchImage; newSectionId: string }>,
    ) {
      const source = state.images.find((image) => image.id === payload.sourceId)
      if (!source) return
      const hadAffinity = Object.prototype.hasOwnProperty.call(
        state.sourceSectionAffinityByImageId,
        payload.sourceId,
      )
      let section = findBatchSectionBySourceAffinity(
        state.sections,
        state.sourceSectionAffinityByImageId,
        payload.sourceId,
      )
      if (!section && !hadAffinity && source.sectionId) section = state.sections[source.sectionId]
      let createdNewSection = false
      if (!section) {
        const previousSourceIndex = state.images.indexOf(source)
        const previousSourcePreviousImageId = state.images[previousSourceIndex - 1]?.id
        const previousSourceNextImageId = state.images[previousSourceIndex + 1]?.id
        const previousSourceSectionId = source.sectionId
        const previousSourceSectionIndex = previousSourceSectionId
          ? getBatchSectionMemberIds(state.images, previousSourceSectionId).indexOf(source.id)
          : undefined
        const previousSourceAffinitySectionId = state.sourceSectionAffinityByImageId[payload.sourceId]
        state.nextSectionOrdinal += 1
        section = {
          id: payload.newSectionId,
          name: `Product ${state.nextSectionOrdinal}`,
          nameOrigin: 'fallback',
          namingSourceImageId: payload.sourceId,
          pendingGenerationSourceImageId: payload.sourceId,
          previousSourceSectionId,
          previousSourceAffinitySectionId,
          previousSourceIndex,
          previousSourceSectionIndex: previousSourceSectionIndex != null && previousSourceSectionIndex !== -1
            ? previousSourceSectionIndex
            : undefined,
          previousSourcePreviousImageId,
          previousSourceNextImageId,
        }
        state.sections[section.id] = section
        source.sectionId = section.id
        createdNewSection = true
      }
      state.sourceSectionAffinityByImageId[payload.sourceId] = section.id
      const { data, edit, badgeAsset } = splitImage(payload.image)
      edit.sectionId = section.id
      state.dataMap[data.id] = data
      registerSplitBadgeAsset(state, badgeAsset)
      const insertIndex = getBatchSectionInsertionIndex(state.images, section.id)
      state.images.splice(insertIndex, 0, edit)
      // Tombstone / first-auto paths reassign the source in place; compact so the
      // previous section stays contiguous and display order matches export.
      if (createdNewSection) {
        state.images = compactBatchSectionMembership(state.images)
      }
      pruneEmptySections(state.images, state.sections, state.sourceSectionAffinityByImageId)
      clearMissingSelectedSection(state)
    },
    deleteBatchSection(state, { payload: sectionId }: PayloadAction<string>) {
      if (!state.sections[sectionId]) return
      state.images = state.images.filter((image) => image.sectionId !== sectionId)
      delete state.sections[sectionId]
      state.selectedIds = []
      if (state.selectedSectionId === sectionId) state.selectedSectionId = null
      pruneEmptySections(state.images, state.sections, state.sourceSectionAffinityByImageId)
      pruneUnreferencedData(state)
    },
    setItemStatus(
      state,
      { payload }: PayloadAction<{ id: string; status: BatchItemStatus; error?: string | null }>,
    ) {
      const item = state.images.find((image) => image.id === payload.id)
      if (!item) return
      item.status = payload.status
      item.error = payload.error ?? null
    },
    /** Batch status set for many ids in one action — one store notification instead of N. */
    setItemsStatus(
      state,
      { payload }: PayloadAction<{ ids: string[]; status: BatchItemStatus; error?: string | null }>,
    ) {
      const idSet = new Set(payload.ids)
      for (const item of state.images) {
        if (idSet.has(item.id)) {
          item.status = payload.status
          item.error = payload.error ?? null
        }
      }
    },
    /**
     * Atomic bg-removal result: append the mask version, seed the cutout cache,
     * flip bgRemoved on, and mark done — all in ONE action. Replaces the prior
     * setItemMask + setItemStatus + setBgRemoved trio, cutting per-item store
     * notifications (and full-grid selector reruns) 3x during a batch removal.
     */
    applyRemovalResult(
      state,
      {
        payload,
      }: PayloadAction<{
        id: string
        maskDataUrl: string
        cutoutDataUrl?: string
        bbox?: NormalizedBBox | null
        bboxWidth?: number
        bboxHeight?: number
      }>,
    ) {
      const data = state.dataMap[payload.id]
      if (!data) return
      if (!data.masks) data.masks = {}
      const version = data.nextMaskVersion ?? 0
      data.masks[version] = payload.maskDataUrl
      data.nextMaskVersion = version + 1
      const edit = state.images.find((image) => image.id === payload.id)
      if (edit) {
        edit.maskVersion = version
        edit.bgRemoved = true
        edit.status = 'done'
        edit.error = null
      }
      if (payload.cutoutDataUrl) {
        if (!state.cutoutCache[payload.id]) state.cutoutCache[payload.id] = {}
        state.cutoutCache[payload.id][version] = {
          url: payload.cutoutDataUrl,
          bbox: payload.bbox ?? null,
          width: payload.bboxWidth,
          height: payload.bboxHeight,
        }
      }
    },
    setItemMask(
      state,
      {
        payload,
      }: PayloadAction<{
        id: string
        maskDataUrl: string
        cutoutDataUrl?: string
        bbox?: NormalizedBBox | null
        bboxWidth?: number
        bboxHeight?: number
      }>,
    ) {
      const data = state.dataMap[payload.id]
      if (!data) return
      if (!data.masks) data.masks = {}
      const version = data.nextMaskVersion ?? 0
      data.masks[version] = payload.maskDataUrl
      data.nextMaskVersion = version + 1
      const edit = state.images.find((image) => image.id === payload.id)
      if (edit) edit.maskVersion = version
      if (payload.cutoutDataUrl) {
        if (!state.cutoutCache[payload.id]) state.cutoutCache[payload.id] = {}
        state.cutoutCache[payload.id][version] = {
          url: payload.cutoutDataUrl,
          bbox: payload.bbox ?? null,
          width: payload.bboxWidth,
          height: payload.bboxHeight,
        }
      }
    },
    setCutoutCache(
      state,
      {
        payload,
      }: PayloadAction<{
        id: string
        version: number
        url: string
        bbox?: NormalizedBBox | null
        bboxWidth?: number
        bboxHeight?: number
      }>,
    ) {
      if (!state.cutoutCache[payload.id]) state.cutoutCache[payload.id] = {}
      state.cutoutCache[payload.id][payload.version] = {
        url: payload.url,
        bbox: payload.bbox ?? null,
        width: payload.bboxWidth,
        height: payload.bboxHeight,
      }
    },
    setBgRemoved(state, { payload }: PayloadAction<{ ids: string[]; value: boolean }>) {
      const idSet = new Set(payload.ids)
      for (const item of state.images) {
        if (idSet.has(item.id)) item.bgRemoved = payload.value
      }
    },
    setBgColor(state, { payload }: PayloadAction<{ ids: string[]; color: string | null }>) {
      const idSet = new Set(payload.ids)
      for (const item of state.images) {
        if (idSet.has(item.id)) {
          item.bgColor = payload.color
          // Color and image background are mutually exclusive.
          item.bgImageId = null
        }
      }
    },
    /** Merges freshly-hydrated/uploaded background records into the map — additive,
     *  never clears entries not present in `records` (that's pruneUnreferencedData's job). */
    registerBgImages(state, { payload: records }: PayloadAction<CustomBackgroundRecord[]>) {
      for (const record of records) {
        state.bgImageMap[record.id] = record
      }
    },
    setBgImage(state, { payload }: PayloadAction<{ ids: string[]; bgImageId: string | null }>) {
      const idSet = new Set(payload.ids)
      for (const item of state.images) {
        if (idSet.has(item.id)) {
          item.bgImageId = payload.bgImageId
          // Image and color background are mutually exclusive.
          item.bgColor = null
        }
      }
    },
    /** Ids referenced from outside the edit/history graph — the Custom list and
     *  every saved preset. Kept in sync by the session hook; pruneUnreferencedData
     *  treats this as an additional root alongside live/history bgImageId refs. */
    setPinnedBgImageIds(state, { payload }: PayloadAction<string[]>) {
      state.pinnedBgImageIds = payload
    },
    /** Ids referenced from outside the edit/history graph — the Custom badges
     *  list and every saved preset. Kept in sync by the session hook;
     *  pruneUnreferencedData treats this as an additional root alongside
     *  live/history upload-badge assetId refs. */
    setPinnedBadgeAssetIds(state, { payload }: PayloadAction<string[]>) {
      state.pinnedBadgeAssetIds = payload
    },
    setShadow(state, { payload }: PayloadAction<{ ids: string[]; shadow: ShadowConfig | null }>) {
      const idSet = new Set(payload.ids)
      for (const item of state.images) {
        if (idSet.has(item.id)) item.shadow = payload.shadow
      }
    },
    setAdjustments(
      state,
      { payload }: PayloadAction<{ ids: string[]; adjustments: AdjustmentsConfig | null }>,
    ) {
      const idSet = new Set(payload.ids)
      for (const item of state.images) {
        if (idSet.has(item.id)) item.adjustments = payload.adjustments
      }
    },
    // Merges a partial patch into each target image's existing adjustments,
    // preserving keys not present in the patch. Used for batch edits over a
    // mixed selection, where overwriting a full config would clobber the
    // untouched per-image values.
    mergeAdjustments(
      state,
      { payload }: PayloadAction<{ ids: string[]; patch: Partial<AdjustmentsConfig> }>,
    ) {
      const idSet = new Set(payload.ids)
      for (const item of state.images) {
        if (!idSet.has(item.id)) continue
        item.adjustments = { ...(item.adjustments ?? defaultAdjustments()), ...payload.patch }
      }
    },
    setPosition(
      state,
      { payload }: PayloadAction<{ ids: string[]; position: PositionConfig | null }>,
    ) {
      const idSet = new Set(payload.ids)
      for (const item of state.images) {
        if (idSet.has(item.id)) item.position = payload.position
      }
    },
    registerBadgeAsset(state, { payload }: PayloadAction<BatchBadgeAsset>) {
      const existing = state.badgeAssets[payload.id]
      // Re-registering identical bytes is common (the hydrate safety net does it
      // for every listed record). Writing anyway would mint a new badgeAssets
      // map, which invalidates selectBatchImages for every upload-badge card.
      if (existing?.dataUrl === payload.dataUrl && existing.previewUrl === payload.previewUrl) return
      state.badgeAssets[payload.id] = payload
    },
    setBadge(
      state,
      { payload }: PayloadAction<{ ids: string[]; badge: BatchBadgeConfig | null }>,
    ) {
      const idSet = new Set(payload.ids)
      for (const item of state.images) {
        if (idSet.has(item.id)) item.badge = payload.badge ? { ...payload.badge } : null
      }
    },
    patchBadgeLayout(
      state,
      { payload }: PayloadAction<{ ids: string[]; patch: Partial<BatchBadgeLayout> }>,
    ) {
      const idSet = new Set(payload.ids)
      for (const item of state.images) {
        if (!idSet.has(item.id) || !item.badge) continue
        item.badge = { ...item.badge, ...payload.patch } as BatchBadgeConfig
      }
    },
    setTargetSize(
      state,
      {
        payload,
      }: PayloadAction<{ ids: string[]; targetWidth: number; targetHeight: number; aspectRatio: number }>,
    ) {
      const idSet = new Set(payload.ids)
      for (const item of state.images) {
        if (idSet.has(item.id)) {
          item.targetWidth = payload.targetWidth
          item.targetHeight = payload.targetHeight
          item.aspectRatio = payload.aspectRatio
        }
      }
    },
    deleteSelected(state) {
      const selectedSet = new Set(state.selectedIds)
      state.images = state.images.filter(image => !selectedSet.has(image.id))
      state.selectedIds = []
      pruneEmptySections(state.images, state.sections, state.sourceSectionAffinityByImageId)
      clearMissingSelectedSection(state)
      pruneUnreferencedData(state)
    },
    /**
     * Removes ids from the live list and every history snapshot, then prunes.
     * Used when an Edit-with-AI run is aborted (e.g. leaving Batch Studio) so a
     * `processingEditWithAi` placeholder cannot be revived as a forever-spinner
     * via redo after its API call is gone.
     */
    discardBatchImages(state, { payload: ids }: PayloadAction<string[]>) {
      if (!ids.length) return
      const idSet = new Set(ids)
      const keep = (image: BatchImageEdit) => !idSet.has(image.id)
      state.images = state.images.filter(keep)
      rollbackFailedAutomaticSections(
        state.images,
        state.sections,
        state.sourceSectionAffinityByImageId,
      )
      state.past = state.past.map((snapshot) => {
        const next: BatchHistorySnapshot = {
          images: snapshot.images.filter(keep),
          sections: snapshotSections(snapshot.sections),
          sourceSectionAffinityByImageId: { ...snapshot.sourceSectionAffinityByImageId },
        }
        rollbackFailedAutomaticSections(next.images, next.sections, next.sourceSectionAffinityByImageId)
        pruneEmptySections(next.images, next.sections, next.sourceSectionAffinityByImageId)
        return next
      })
      state.future = state.future.map((snapshot) => {
        const next: BatchHistorySnapshot = {
          images: snapshot.images.filter(keep),
          sections: snapshotSections(snapshot.sections),
          sourceSectionAffinityByImageId: { ...snapshot.sourceSectionAffinityByImageId },
        }
        rollbackFailedAutomaticSections(next.images, next.sections, next.sourceSectionAffinityByImageId)
        pruneEmptySections(next.images, next.sections, next.sourceSectionAffinityByImageId)
        return next
      })
      state.selectedIds = state.selectedIds.filter((id) => !idSet.has(id))
      pruneEmptySections(state.images, state.sections, state.sourceSectionAffinityByImageId)
      clearMissingSelectedSection(state)
      pruneUnreferencedData(state)
    },
    /**
     * Clears the `processingEditWithAi` marker for `ids` in every history snapshot;
     * the live list is left alone. Dispatched when an Edit-with-AI run fails
     * terminally but its tile stays on screen showing the error: without this a
     * snapshot taken mid-flight would restore the placeholder as a forever spinner
     * with no API call behind it. Downgrading to 'error' lets sanitizeRestoredEdits
     * drop the byte-less tile like any other placeholder that never landed.
     */
    settleHistoryPlaceholders(state, { payload: ids }: PayloadAction<string[]>) {
      if (!ids.length) return
      const idSet = new Set(ids)
      const settle = (snapshot: BatchHistorySnapshot): BatchHistorySnapshot => ({
        ...snapshot,
        images: snapshot.images.map((image) =>
          idSet.has(image.id) && image.status === 'processingEditWithAi'
            ? { ...image, status: 'error' as const, error: null }
            : image,
        ),
      })
      state.past = state.past.map(settle)
      state.future = state.future.map(settle)
    },
    clearTargetSize(state, { payload }: PayloadAction<{ ids: string[] }>) {
      const idSet = new Set(payload.ids)
      for (const item of state.images) {
        if (idSet.has(item.id)) {
          item.targetWidth = undefined
          item.targetHeight = undefined
          item.aspectRatio = undefined
        }
      }
    },
    /** Snapshot the current edit list before a mutation. Clears the redo stack. */
    pushHistory(state) {
      state.past = [...state.past.slice(-(MAX_HISTORY - 1)), snapshotBatch(state)]
      state.future = []
      pruneUnreferencedData(state)
    },
    undo(state) {
      if (!state.past.length) return
      state.future = [snapshotBatch(state), ...state.future.slice(0, MAX_HISTORY - 1)]
      const restored = state.past[state.past.length - 1]
      state.past = state.past.slice(0, -1)
      state.images = sanitizeRestoredEdits(restored.images, state.dataMap)
      state.sections = snapshotSections(restored.sections)
      state.sourceSectionAffinityByImageId = { ...restored.sourceSectionAffinityByImageId }
      pruneEmptySections(state.images, state.sections, state.sourceSectionAffinityByImageId)
      state.selectedIds = []
      state.selectedSectionId = null
      pruneUnreferencedData(state)
    },
    redo(state) {
      if (!state.future.length) return
      state.past = [...state.past.slice(-(MAX_HISTORY - 1)), snapshotBatch(state)]
      const restored = state.future[0]
      state.future = state.future.slice(1)
      state.images = sanitizeRestoredEdits(restored.images, state.dataMap)
      state.sections = snapshotSections(restored.sections)
      state.sourceSectionAffinityByImageId = { ...restored.sourceSectionAffinityByImageId }
      pruneEmptySections(state.images, state.sections, state.sourceSectionAffinityByImageId)
      state.selectedIds = []
      state.selectedSectionId = null
      pruneUnreferencedData(state)
    },
    clearHistory(state) {
      state.past = []
      state.future = []
      pruneUnreferencedData(state)
    },
  },
})

export const {
  setBatchImages,
  addBatchImages,
  insertBatchImagesAfter,
  resolveBatchImagesData,
  landGeneratedBatchImage,
  clearBatch,
  toggleSelect,
  setSelected,
  selectBatchImageIfLive,
  clearSelection,
  selectSection,
  selectSectionImages,
  createBatchSection,
  renameBatchSection,
  claimBatchSectionName,
  resolveBatchSectionName,
  resolveBatchSectionNameBySource,
  moveBatchImages,
  reorderBatchSection,
  insertGeneratedBatchImage,
  deleteBatchSection,
  deleteSelected,
  discardBatchImages,
  settleHistoryPlaceholders,
  setItemStatus,
  setItemsStatus,
  setItemMask,
  applyRemovalResult,
  setCutoutCache,
  setBgRemoved,
  setBgColor,
  registerBgImages,
  setBgImage,
  setPinnedBgImageIds,
  setPinnedBadgeAssetIds,
  setShadow,
  setAdjustments,
  mergeAdjustments,
  setPosition,
  registerBadgeAsset,
  setBadge,
  patchBadgeLayout,
  setTargetSize,
  clearTargetSize,
  pushHistory,
  undo,
  redo,
  clearHistory,
} = batchSlice.actions

export const batchReducer = batchSlice.reducer
